import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import {
  MessageSchedulingChannel,
  ScheduledMessageLink,
  ScheduledMessageMedia,
  linkHostname,
} from "../../utils/messageSchedulingStorage";

interface Props {
  channel: MessageSchedulingChannel;
  text: string;
  media: ScheduledMessageMedia[];
  link?: ScheduledMessageLink;
  contactName?: string;
  contactPhone?: string;
  scheduledAt?: string;
}

const CHANNEL_ICON: Record<MessageSchedulingChannel, string> = {
  whatsapp: "whatsapp",
  facebook: "facebook-messenger",
  instagram: "instagram",
};

export const WhatsappMessagePreview = (props: Props) => {
  const {
    channel,
    text,
    media,
    link,
    contactName,
    contactPhone,
    scheduledAt,
  } = props;
  const intl = useIntl();

  const images = useMemo(
    () => media.filter((item) => item.type.startsWith("image/")),
    [media]
  );
  const videos = useMemo(
    () => media.filter((item) => item.type.startsWith("video/")),
    [media]
  );

  const timeLabel = useMemo(
    () =>
      scheduledAt && moment(scheduledAt).isValid()
        ? moment(scheduledAt).format("HH:mm")
        : moment().format("HH:mm"),
    [scheduledAt]
  );

  const dayLabel = useMemo(
    () =>
      scheduledAt && moment(scheduledAt).isValid()
        ? moment(scheduledAt).format("DD/MM/YYYY")
        : moment().format("DD/MM/YYYY"),
    [scheduledAt]
  );

  const heroImage = images[0];
  const extraImages = images.slice(1);
  const hasContent = Boolean(text.trim()) || media.length > 0 || Boolean(link?.url);

  return (
    <div className={`ms-phone ms-phone--${channel}`}>
      <div className="ms-phone__notch" aria-hidden />
      <div className="ms-phone__topbar">
        <FontAwesomeIcon icon={["fas", "chevron-left"]} className="ms-phone__back" />
        <div className="ms-phone__avatar">
          {(contactName || "W").trim().charAt(0).toUpperCase()}
        </div>
        <div className="ms-phone__peer">
          <strong>
            {contactName ||
              intl.formatMessage({
                id: "app.messageScheduling.preview.contactPlaceholder",
              })}
          </strong>
          <span>
            {contactPhone ||
              intl.formatMessage({
                id: "app.messageScheduling.preview.online",
              })}
          </span>
        </div>
        <FontAwesomeIcon
          icon={["fab", CHANNEL_ICON[channel] as any]}
          className="ms-phone__channelIcon"
        />
      </div>

      <div className="ms-phone__chat">
        <div className="ms-phone__daySeparator">
          <span>{dayLabel}</span>
        </div>

        {!hasContent ? (
          <div className="ms-phone__placeholder">
            {intl.formatMessage({
              id: "app.messageScheduling.preview.empty",
            })}
          </div>
        ) : (
          <div className="ms-phone__bubble">
            {heroImage?.previewDataUrl ? (
              <div className="ms-phone__bubbleMedia">
                <img src={heroImage.previewDataUrl} alt={heroImage.name} />
              </div>
            ) : heroImage ? (
              <div className="ms-phone__bubbleMedia ms-phone__bubbleMedia--fallback">
                <FontAwesomeIcon icon={["fas", "image"]} />
                <span>{heroImage.name}</span>
              </div>
            ) : null}

            {videos.length > 0 && (
              <div className="ms-phone__bubbleMedia ms-phone__bubbleMedia--video">
                <FontAwesomeIcon icon={["fas", "play"]} />
                <span>{videos[0].name}</span>
              </div>
            )}

            {extraImages.length > 0 && (
              <div className="ms-phone__bubbleThumbs">
                {extraImages.slice(0, 4).map((item) =>
                  item.previewDataUrl ? (
                    <img
                      key={item.id}
                      src={item.previewDataUrl}
                      alt={item.name}
                    />
                  ) : (
                    <span key={item.id} className="ms-phone__bubbleThumb--empty">
                      <FontAwesomeIcon icon={["fas", "image"]} />
                    </span>
                  )
                )}
                {extraImages.length > 4 && (
                  <span className="ms-phone__bubbleThumbsMore">
                    +{extraImages.length - 4}
                  </span>
                )}
              </div>
            )}

            {link?.url && (
              <a
                className="ms-phone__linkCard"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.preventDefault()}
              >
                <span className="ms-phone__linkHost">
                  {linkHostname(link.url)}
                </span>
                <strong className="ms-phone__linkTitle">
                  {link.title || link.url}
                </strong>
                {link.description && (
                  <span className="ms-phone__linkDesc">{link.description}</span>
                )}
              </a>
            )}

            {text.trim() && <div className="ms-phone__bubbleText">{text}</div>}

            {link?.url && !text.includes(link.url) && (
              <div className="ms-phone__bubbleLinkInline">{link.url}</div>
            )}

            <div className="ms-phone__bubbleMeta">
              <span>{timeLabel}</span>
              <FontAwesomeIcon icon={["fas", "check-double"]} />
            </div>
          </div>
        )}
      </div>

      <div className="ms-phone__composer">
        <FontAwesomeIcon icon={["far", "smile"]} />
        <span>
          {intl.formatMessage({ id: "app.messageScheduling.preview.inputHint" })}
        </span>
        <FontAwesomeIcon icon={["fas", "paper-plane"]} className="ms-phone__send" />
      </div>
    </div>
  );
};
